import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import { verifyUserStripeConnect } from "../../redux/actions/stripeActions";
import { updateUserInfo } from "../../redux/slices/userSlice";
import { DASHBOARD_STACK } from "../../utils/screenNames";

/**
 * Stripe redirects the user here after completing onboarding.
 * We poll the /verify endpoint a few times so the backend has a chance to
 * receive Stripe's account.updated webhook before we read fresh status.
 */
const StripeConnectReturn = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector((state) => state.user.user);
  const [status, setStatus] = useState("checking"); // checking | success | failed
  const ranRef = useRef(false);

  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;

    let cancelled = false;

    const verify = async () => {
      let onboarded = false;
      let lastStripe = null;
      // Poll for up to ~60s — Stripe Express returns the user before
      // charges_enabled/payouts_enabled flip on Stripe's side, so we wait
      // for the webhook (or the verify endpoint) to land.
      for (let attempt = 0; attempt < 15 && !onboarded && !cancelled; attempt++) {
        try {
          const stripe = await verifyUserStripeConnect();
          lastStripe = stripe;
          if (stripe?.connectedAccount?.isOnboard) {
            onboarded = true;
            break;
          }
        } catch (err) {
          // ignore and retry
        }
        await new Promise((r) => setTimeout(r, 2000));
      }

      if (cancelled) return;

      if (onboarded) {
        // Merge updated stripe info into Redux user
        dispatch(
          updateUserInfo({
            userData: { ...user, stripe: lastStripe },
          })
        );
        setStatus("success");
        setTimeout(() => navigate(DASHBOARD_STACK.PAYMENTS), 1200);
      } else {
        setStatus("failed");
        setTimeout(() => navigate(DASHBOARD_STACK.PAYMENTS), 2000);
      }
    };

    verify();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="bg-[#000F1F] text-[#FEFCEB] min-h-screen flex items-center justify-center px-6">
      <div className="text-center max-w-[400px]">
        {status === "checking" && (
          <>
            <h2 className="text-[20px] font-medium mb-2">Verifying with Stripe...</h2>
            <p className="text-app-text-secondary text-[14px]">
              Just a moment while we confirm your account.
            </p>
          </>
        )}
        {status === "success" && (
          <>
            <h2 className="text-[20px] font-medium mb-2">Stripe account connected</h2>
            <p className="text-app-text-secondary text-[14px]">
              Redirecting you to payments...
            </p>
          </>
        )}
        {status === "failed" && (
          <>
            <h2 className="text-[20px] font-medium mb-2">Stripe setup not complete</h2>
            <p className="text-app-text-secondary text-[14px]">
              We couldn't confirm your Stripe onboarding yet. You can try again from the payments page.
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default StripeConnectReturn;
